import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { useFeatureFlag, useIsAdmin } from "../../hooks/useFeatureFlags";
import { CONTENT_CONTAINER } from "../../lib/layout";
import { motion, useReducedMotion } from "motion/react";
import { Home, Compass, Eye, Calendar, Users, Handshake, Settings } from "lucide-react";
import { springs, tokens, motionColors } from "../../lib/motion.config";
import type { LucideIcon } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { useChrome } from "../chrome/ChromeContext";

// ── Nav item config ─────────────────────────────────────

interface NavItem {
  path: string;
  icon: LucideIcon;
  label: string;
  prefix?: string;
  flag?: string;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { path: "/", icon: Home, label: "Home", prefix: "Find" },
  { path: "/decks", icon: Compass, label: "Direction", prefix: "Find" },
  { path: "/find-clarity", icon: Eye, label: "Clarity", prefix: "Find" },
  { path: "/find-events", icon: Calendar, label: "Events", prefix: "Find" },
  {
    path: "/find-practitioners",
    icon: Handshake,
    label: "Creators",
    prefix: "Find",
  },
  {
    path: "/find-community",
    icon: Users,
    label: "Community",
    prefix: "Find",
    flag: "community_feed",
  },
  { path: "/admin", icon: Settings, label: "Admin", adminOnly: true },
];

// ── Entrance animation variants ─────────────────────────

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: tokens.staggerDelay,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: tokens.entranceY },
  visible: {
    opacity: 1,
    y: 0,
    transition: springs.gentle,
  },
};

// ── Hover detection ─────────────────────────────────────
// Only apply hover effects on devices that support hover (not touch)

const canHover =
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover)").matches;

// ── Active-state helpers ─────────────────────────────────

const DIRECTION_ROUTES = new Set([
  '/decks',
  '/moon-calendar',
  '/moon-sign-list',
  '/find-direction', // legacy alias — redirects to /decks
])

function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (item.path === '/decks') return DIRECTION_ROUTES.has(pathname)
  return pathname === item.path
}

// ── BottomNav ───────────────────────────────────────────

export function BottomNav() {
  const { desktopChrome } = useChrome();
  if (desktopChrome) return null;

  const location = useLocation();
  const navigate = useNavigate();
  const hasAnimated = useRef(false);
  const { isSignedIn } = useAuth();
  const helpCenterOn = useFeatureFlag("help_center");

  // Always-hidden routes (full-screen flows where the tab bar would interrupt).
  // The handoff-v2 sister pages (/about /pricing /how-it-works) render their own
  // MarketingFooter via MarketingLayout — the in-app tab bar would stack
  // underneath, so it hides there for both signed-in and signed-out users.
  // Marketing surfaces never show app chrome.
  // /shared/:shortid is the public chrome-less shared-reading page — it owns
  // its own sign-up CTA and must render with NO app chrome for anonymous
  // recipients.
  const hideOnPaths = [
    "/reading", "/onboarding", "/sign-in", "/sign-up", "/install",
    "/about", "/pricing", "/how-it-works", "/shared",
  ];
  // Marketing/legal routes where the tab bar should hide for anon visitors —
  // they have no signed-in nav to use yet, and the bar implies "you're inside
  // the app." Signed-in users keep the tab bar on these as normal app chrome.
  const ANON_HIDDEN_MARKETING = [
    "/",
    "/terms",
    "/oracle-reading-app",
    "/astrological-weather",
    "/pull-a-card",
    "/services",
  ];
  // Netlify's pretty_urls processing adds a trailing slash to nested routes
  // (e.g. /oracle-reading-app → /oracle-reading-app/), so normalize before
  // comparing — the local Vite dev server doesn't do this, which is how
  // this slipped past local tests on the first try.
  const normalizedPath = location.pathname.length > 1 && location.pathname.endsWith("/")
    ? location.pathname.slice(0, -1)
    : location.pathname;
  const isAnonMarketing = !isSignedIn && ANON_HIDDEN_MARKETING.includes(normalizedPath);
  if (hideOnPaths.some((p) => location.pathname.startsWith(p)) || isAnonMarketing) return null;

  // /help is the marketing HelpPage (its own footer) unless a signed-in viewer
  // has the help_center flag on — then it's the in-app Help center, which keeps
  // the tab bar. Flag off / signed out ⇒ hide (marketing). Byte-identical off.
  if (location.pathname.startsWith("/help") && !(isSignedIn && helpCenterOn)) return null;

  // The admin portal (/admin/*) owns its own full-screen chrome (left rail);
  // the in-app bottom nav would duplicate navigation, so it hides there.
  if (location.pathname === "/admin" || location.pathname.startsWith("/admin/")) return null;

  // Only stagger entrance on first mount
  const shouldAnimate = !hasAnimated.current;
  if (shouldAnimate) hasAnimated.current = true;

  return (
    <nav
      aria-label="Bottom navigation"
      className="shrink-0 bg-cosmic-topbar border-t border-hairline pb-[env(safe-area-inset-bottom)]"
    >
      <div className={CONTENT_CONTAINER}>
        <motion.div
          className="flex overflow-x-auto no-scrollbar justify-around gap-1"
          variants={containerVariants}
          initial={shouldAnimate ? "hidden" : false}
          animate="visible"
        >
          {NAV_ITEMS.map((item) => (
            <NavButton
              key={item.path}
              item={item}
              isActive={isNavItemActive(item, location.pathname)}
              navigate={navigate}
            />
          ))}
        </motion.div>
      </div>
    </nav>
  );
}

// ── NavButton ───────────────────────────────────────────

function NavButton({
  item,
  isActive,
  navigate,
}: {
  item: NavItem;
  isActive: boolean;
  navigate: (path: string) => void;
}) {
  const flagActive = useFeatureFlag(item.flag ?? "");
  const isAdmin = useIsAdmin();
  const shouldReduce = useReducedMotion();

  // Auto-scroll active item into horizontal view when route changes.
  // Hooks must run in the same order every render — keep these BEFORE
  // any conditional `return null` below so the hook count never changes.
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (isActive && buttonRef.current) {
      buttonRef.current.scrollIntoView({
        inline: "center",
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [isActive]);

  if (item.flag && !flagActive) return null;
  if (item.adminOnly && !isAdmin) return null;

  const Icon = item.icon;

  const hoverProps = canHover && !shouldReduce
    ? {
        y: tokens.hoverLift,
        filter: motionColors.hoverGlow,
        transition: springs.snappy,
      }
    : undefined;

  const tapProps = !shouldReduce
    ? { scale: tokens.pressScale, transition: springs.press }
    : undefined;

  return (
    <motion.button
      ref={buttonRef}
      variants={itemVariants}
      onClick={() => navigate(item.path)}
      whileHover={hoverProps}
      whileTap={tapProps}
      aria-current={isActive ? "page" : undefined}
      className="group shrink-0 min-w-[72px] flex flex-col items-center justify-center gap-1 py-2.5 md:py-3 px-2 relative outline-hidden focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded-md"
    >
      {/* Hover glow background */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-md"
        style={{
          background: "radial-gradient(circle at 50% 40%, color-mix(in oklab, var(--color-gold) 22%, transparent) 0%, color-mix(in oklab, var(--color-gold) 8%, transparent) 50%, transparent 75%)",
        }}
        aria-hidden="true"
      />

      {/* Icon with stroke-draw animation */}
      <NavIcon Icon={Icon} isActive={isActive} shouldReduce={shouldReduce} />

      {/* Label */}
      <span
        className={`text-xxs tracking-cosmic uppercase font-body leading-tight text-center min-h-[24px] flex flex-col justify-center transition-colors duration-200 ${
          isActive ? "text-gold" : "text-text-muted/70"
        }`}
      >
        {item.prefix && <span className="block">{item.prefix}</span>}
        <span>{item.label}</span>
      </span>

      {/* Sliding active indicator */}
      {isActive && <ActiveIndicator shouldReduce={shouldReduce} />}
    </motion.button>
  );
}

// ── NavIcon (SVG stroke-draw on activation) ─────────────

function NavIcon({
  Icon,
  isActive,
  shouldReduce,
}: {
  Icon: LucideIcon;
  isActive: boolean;
  shouldReduce: boolean | null;
}) {
  // Track when isActive flips from false->true to trigger the draw animation
  const [isDrawing, setIsDrawing] = useState(false);
  const wasActive = useRef(isActive);

  useEffect(() => {
    if (isActive && !wasActive.current && !shouldReduce) {
      setIsDrawing(true);
      const timer = setTimeout(() => setIsDrawing(false), 650);
      return () => clearTimeout(timer);
    }
    wasActive.current = isActive;
  }, [isActive, shouldReduce]);

  const drawClass = isActive ? "nav-icon-draw" : "";
  const drawingClass = isDrawing ? "is-drawing" : "";
  const glowClass = isActive && !isDrawing ? "nav-icon-active-glow" : "";

  const iconHover = canHover && !shouldReduce
    ? { rotate: tokens.hoverTilt, transition: springs.snappy }
    : undefined;

  return (
    <motion.div
      className={`leading-none ${drawClass} ${drawingClass} ${glowClass}`}
      whileHover={iconHover}
    >
      <Icon
        size={20}
        strokeWidth={1.8}
        className={`transition-colors duration-200 ${
          isActive ? "text-gold" : "text-text-muted/80"
        }`}
      />
    </motion.div>
  );
}

// ── ActiveIndicator (sliding gradient underline) ────────

function ActiveIndicator({ shouldReduce }: { shouldReduce: boolean | null }) {
  return (
    <motion.div
      layoutId="nav-indicator"
      className="absolute -bottom-px left-2 right-2 h-[2px] rounded-full"
      style={{
        background: `linear-gradient(90deg, ${motionColors.indicatorFrom()}, ${motionColors.indicatorTo()})`,
      }}
      transition={shouldReduce ? { duration: 0 } : springs.indicator}
      aria-hidden="true"
    />
  );
}
